import { useEffect, useState } from 'react';

import { faClipboard } from '@fortawesome/free-regular-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Textarea } from '@repo/videa-components/src/ui/textarea';
import { useAtomValue } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';

import { LoadingAnimation } from '@/common-components/animations/loading/LoadingAnimation';
import { FeedbackWidget } from '@/common-components/elements/FeedbackWidget/FeedbackWidget';
import {
  cleanClaimsAttachmentValuesAtom,
  isNarrativeLoadingAtom,
  narrativeAtom,
  narrativeTraceIdAtom,
  selectedTreatmentCodesAtom,
} from '@/modules/claims/summary/state';
import { usePreferences } from '@/react-query/usePreferences';
import { useLangfuseFeedback } from '@/utils/hooks/useLangfuseFeedback';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

type ClaimNarrativeProps = {
  narrativeTextDraft: string;
  isEditing: boolean;
  onChange: (text: string) => void;
};

const RATE_NARRATIVE_OPTIONS_POSITIVE = [
  {
    value: 'accurate-information',
    label: <Trans>Accurate information</Trans>,
  },
  {
    value: 'minimal-edits',
    label: <Trans>Minimal edits needed</Trans>,
  },
  {
    value: 'saved-time',
    label: <Trans>Saved time</Trans>,
  },
  {
    value: 'correct-terminology',
    label: <Trans>Correct terminology</Trans>,
  },
  {
    value: 'correct-images',
    label: <Trans>Correct images selected</Trans>,
  },
];

const RATE_NARRATIVE_OPTIONS_NEGATIVE = [
  {
    value: 'missing-treatments',
    label: <Trans>Missing treatments</Trans>,
  },
  {
    value: 'incorrect-codes',
    label: <Trans>Incorrect codes</Trans>,
  },
  {
    value: 'mislabeled-images',
    label: <Trans>Mislabeled images</Trans>,
  },
  {
    value: 'incorrect-info',
    label: <Trans>Incorrect patient or provider information</Trans>,
  },
];
export const ClaimNarrative = ({
  narrativeTextDraft,
  isEditing,
  onChange,
}: ClaimNarrativeProps) => {
  const narrativeText = useAtomValue(narrativeAtom);
  const isLoadingNarrativeText = useAtomValue(isNarrativeLoadingAtom);
  const selectedTreatmentCodes = useAtomValue(selectedTreatmentCodesAtom);
  const cleanClaimsAttachmentValues = useAtomValue(
    cleanClaimsAttachmentValuesAtom,
  );
  const narrativeTraceId = useAtomValue(narrativeTraceIdAtom);
  const { data: preferences } = usePreferences();
  const { trackEvent } = useTrackEvent();

  const [isCopySuccess, setIsCopySuccess] = useState(false);

  const user = useSelector((state: RootStateOrAny) => state?.login?.user);

  const { unifiedId, estimatedVisitId = '' } = cleanClaimsAttachmentValues;

  const { trackFeedbackAutoTrace } = useLangfuseFeedback({
    context: 'narrative',
    estimatedVisitId,
    unifiedAppointmentId: unifiedId,
    backendTraceId: narrativeTraceId,
  });

  const handleCopyNarrative = async () => {
    if (narrativeText) {
      try {
        await navigator.clipboard.writeText(narrativeText);
        setIsCopySuccess(true);
        // Reset the success state after 2 seconds
        setTimeout(() => setIsCopySuccess(false), 2000);
      } catch (error) {
        console.error('Failed to copy text:', error);
      }
    }
  };

  const handleNarrativeButtonClick = (rating: 'positive' | 'negative') => {
    // Track to Mixpanel immediately when thumbs up/downbutton is clicked
    trackEvent(mixpanelEvents.rateClaimAttachment, {
      txCode: selectedTreatmentCodes,
      source: 'narrative',
      rating,
    });
  };

  const handleSubmitNarrativeFeedback = async (
    rating: 'positive' | 'negative',
    selectedOptions: string[],
    additionalComments?: string,
  ) => {
    // Track to Langfuse when dialog is submitted
    await trackFeedbackAutoTrace({
      rating,
      selectedOptions,
      additionalComments,
      metadata: {
        txCode: selectedTreatmentCodes,
        source: 'narrative',
        practiceId: user?.primaryPracticeId,
        organizationId: user?.primaryOrganizationId,
      },
    });
  };

  // Initialize draft with atom value when editing starts
  useEffect(() => {
    if (isEditing && narrativeText) {
      onChange(narrativeText);
    }
  }, [isEditing, narrativeText, onChange]);

  if (isLoadingNarrativeText) {
    return <LoadingAnimation isIconOnly />;
  }

  if (isEditing) {
    return (
      <div className="vai-my-2 vai-py-1">
        <Textarea
          
          value={narrativeTextDraft}
          onChange={(e) => onChange(e.target.value)}
          autoResize
        />
      </div>
    );
  }

  return (
    <div className="vai-w-full vai-text-sm vai-whitespace-pre-wrap vai-py-3 vai-px-3 vai-my-2">
      {narrativeText}
      {!isEditing && narrativeText && preferences?.isFeedbackWidgetEnabled && (
        <div className="vai-mt-3">
          <FeedbackWidget
            variant="minimalist"
            middleAction={{
              icon: isCopySuccess ? (
                <FontAwesomeIcon
                  icon={faCheck}
                  className="vai-text-green-600"
                />
              ) : (
                <FontAwesomeIcon icon={faClipboard} />
              ),
              title: isCopySuccess ? 'Copied!' : 'Copy narrative text',
              onClick: handleCopyNarrative,
            }}
            onRatingButtonClick={handleNarrativeButtonClick}
            onRate={handleSubmitNarrativeFeedback}
            positiveOptions={RATE_NARRATIVE_OPTIONS_POSITIVE}
            negativeOptions={RATE_NARRATIVE_OPTIONS_NEGATIVE}
          />
        </div>
      )}
    </div>
  );
};
